const gwVitalsArray = ['PR_GW', 'RR_GW', 'BP_GW', 'SPO2_GW'];
const mdVitalsArray = ['PR_MD', 'RR_MD', 'BP_MD', 'SPO2_MD'];
const gwVitalsSet = new Set(gwVitalsArray);
const mdVitalsSet = new Set(mdVitalsArray);

export const getAppType = (licensedVitals) => {
  const allGw = licensedVitals.every(vital => gwVitalsSet.has(vital));

  if (allGw) return 'GW';

  const allMm = licensedVitals.every(vital => mdVitalsSet.has(vital));

  if (allMm) {
    return 'MD';
  }

  return 'MD+GW';
}

const getGwInitialData = (vital) => {
  const value = (vital === 'BP_GW') ? '' : 0;

  return {
    remainingAttempts: 3,
    value,
    needToMeasure: true,
  };
};

const getVitalInitialData = (vital) => {
  const value = (vital === 'BP_GW' || vital === 'BP_MD') ? '' : 0;

  return {
    remainingAttempts: 3,
    value,
    needToMeasure: true,
  };
};

export const createInitialSessionData = (licensedVitals) => {
  const appType = getAppType(licensedVitals);
  const sessionData = {};

  if (appType === 'GW') {
    licensedVitals.forEach((vital) => {
      sessionData[vital] = getGwInitialData(vital);
    });
  } else {
    licensedVitals.forEach((vital) => {
      sessionData[vital] = getVitalInitialData(vital);
    });
  }

  return sessionData;
};

const noMoreAttempts = (vitalsSessionData) => {
  return Object.values(vitalsSessionData).every((vitalData) => {
    return vitalData.remainingAttempts === 0;
  });
};

export const processAttemptResults = (newMeasurements, sessionData, prevFinalVitalsMeasurementValues) => {
  const updatedVitalsData = {};
  const updatedFinalValues = { ...prevFinalVitalsMeasurementValues };
  let mdAllMeasurementsValid = true;

  mdVitalsArray.forEach((mdVital) => {
    if (newMeasurements[mdVital]) {
      const currRemainingAttempts = sessionData[mdVital].remainingAttempts;
      let updatedOutOfRangeCount = sessionData[mdVital].outOfRangeCount;
      let udpatedNeedToMeasure;
      let updatedRemainingAttempts;
      let updatedValue = sessionData[mdVital].value;
      let newValue;

      if (!updatedFinalValues[mdVital]) updatedFinalValues[mdVital] = newMeasurements[mdVital];

      if (mdVital === 'PR_MD') {
        newValue = newMeasurements[mdVital].hr;

        if (newValue <= 0) {
          mdAllMeasurementsValid = false;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else if (newValue < 40 || newValue > 120) {
          mdAllMeasurementsValid = false;
          updatedOutOfRangeCount += 1;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
          updatedValue = newValue;
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
          udpatedNeedToMeasure = false;
        }
      } else if (mdVital === 'RR_MD') {
        newValue = newMeasurements[mdVital].rr;

        if (newValue <= 0) {
          mdAllMeasurementsValid = false;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else if (newValue < 8 || newValue > 30) {
          mdAllMeasurementsValid = false;
          updatedOutOfRangeCount += 1;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
          updatedValue = newValue;
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
          udpatedNeedToMeasure = false;
        }
      } else if (mdVital === 'SPO2_MD') {
        newValue = newMeasurements[mdVital].spO2;

        if (newValue <= 0) {
          mdAllMeasurementsValid = false;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
          udpatedNeedToMeasure = false;
        }
      } else {
        if (!newMeasurements[mdVital].prediction_ready) {
          mdAllMeasurementsValid = false;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newMeasurements[mdVital];
          updatedFinalValues[mdVital] = newMeasurements[mdVital];
          udpatedNeedToMeasure = false;
        }
      }

      updatedVitalsData[mdVital] = {
        outOfRangeCount: updatedOutOfRangeCount,
        remainingAttempts: updatedRemainingAttempts,
        value: updatedValue,
        needToMeasure: udpatedNeedToMeasure,
      };
    }
  });

  // no md vitals were measured, so set mdAllMeasurementsValid to false to prevent potentially setting
  // updatedRemainingAttempts to 1 after the first attempt
  if (Object.keys(updatedVitalsData === 0)) {
    mdAllMeasurementsValid = false;
  }

  gwVitalsArray.forEach((gwVital) => {
    if (newMeasurements[gwVital]) {
      const currRemainingAttempts = sessionData[gwVital].remainingAttempts;
      let updatedOutOfRangeCount = sessionData[gwVital].outOfRangeCount;
      let udpatedNeedToMeasure;
      let updatedRemainingAttempts;
      let updatedValue = sessionData[gwVital].value;
      let newValue;

      if (!updatedFinalValues[gwVital]) updatedFinalValues[gwVital] = newMeasurements[gwVital];

      if (gwVital === 'PR_GW') {
        newValue = newMeasurements[gwVital].hr;

        if (newValue <= 0) {
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else if (newValue < 40 || newValue > 120) {
          updatedOutOfRangeCount += 1;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
          updatedValue = newValue;
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
          udpatedNeedToMeasure = false;
        }
      } else if (gwVital === 'RR_GW') {
        newValue = newMeasurements[gwVital].rr;

        if (newValue <= 0) {
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else if (newValue < 8 || newValue > 30) {
          updatedOutOfRangeCount += 1;
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
          updatedValue = newValue;
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
          udpatedNeedToMeasure = false;
        }
      } else if (gwVital === 'SPO2_GW') {
        newValue = newMeasurements[gwVital].spO2;

        if (newValue <= 0) {
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newValue;
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
          udpatedNeedToMeasure = false;
        }
      } else {
        if (!newMeasurements[gwVital].prediction_ready) {
          updatedRemainingAttempts = currRemainingAttempts - 1;
          udpatedNeedToMeasure = updatedRemainingAttempts > 0;
        } else {
          updatedRemainingAttempts = 0;
          updatedValue = newMeasurements[gwVital];
          updatedFinalValues[gwVital] = newMeasurements[gwVital];
          udpatedNeedToMeasure = false;
        }
      }

      updatedVitalsData[gwVital] = {
        outOfRangeCount: updatedOutOfRangeCount,
        remainingAttempts: updatedRemainingAttempts,
        value: updatedValue,
        needToMeasure: udpatedNeedToMeasure,
      };
    }
  });

  const sessionComplete = noMoreAttempts(updatedVitalsData);

  return { 
    sessionComplete,
    updatedVitalsData,
    updatedFinalValues,
  };
};
